import { type ObservabilityTool } from 'components/status-checks/authentication-form-utils'
import {
  type EndpointConfiguration,
  ObservabilityToolType,
  type ServiceStatusCheckFromStatusCheck
} from 'types/api/status-checks/status-check'
import { hasPossibleAppDynamicsCustomUrl } from 'utils/status-checks/status-check-integrations-util-appdynamics'
import { convertToAWSApiUrl } from 'utils/status-checks/status-check-integrations-util-aws'
import { isValidUrl } from 'utils/strings'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'
import {
  encodeQueryParams,
  getIntegrationTypeFromObservabilityToolTypeAndDomain
} from 'utils/team-external-integrations/team-external-integrations-util'
import {
  allowedIntegrationURLs,
  allowedIntegrationURLsWithPNI
} from 'utils/yup-additions'

type PredicateFunction = (
  statusCheck: ServiceStatusCheckFromStatusCheck,
  observabilityTool: ObservabilityTool,
  requestMethod: EndpointConfiguration['method']
) => boolean

type TransformFunction = (
  statusCheck: ServiceStatusCheckFromStatusCheck
) => string | undefined

/*
 * Health Check URL Extraction Predicate Functions
 * -----   -----   -----
 * These functions are used to determine if a given Health Check URL should be included
 * in the accumulated array of URLs used to determine if a monitor URL is already in use.
 */

/*
 * Custom integration types have a much wider range of potential URL formats and structures.
 * We do have to consider the case where we attempt to include an AppDynamics URL as a custom
 * integration type, in which we need to allow for certain keywords in the URL to be duplicated.
 */
function existingStatusCheckPredicateFnCustom(
  statusCheck: ServiceStatusCheckFromStatusCheck,
  observabilityTool: ObservabilityTool,
  requestMethod: EndpointConfiguration['method']
) {
  const { endpointConfiguration, integration } = statusCheck
  const { type, url = '' } = integration
  const { method } = endpointConfiguration ?? {}

  if (hasPossibleAppDynamicsCustomUrl(url) || method !== requestMethod)
    return false

  // Duplicate Health Checks shouldn't attempt to match by the `name` field.
  return type === observabilityTool.integrationType
}

/*
 * Datadog Health observability tool types require we first derive the IntegrationType based
 * on the selected domain in order to make the proper comparison against existing Health Checks.
 *
 * NOTE that `getIntegrationTypeFromObservabilityToolTypeAndDomain` returns the supplied `observabilityToolType`
 * to all types that are not Datadog oriented.
 */
function existingStatusCheckPredicateFnDatadog(
  { integration }: ServiceStatusCheckFromStatusCheck,
  observabilityTool: ObservabilityTool
) {
  const { integrationType, name: observabilityToolName } = observabilityTool
  const { domain, name: statusCheckName, observabilityToolType } = integration

  // This probably should always exist, but our type defs say otherwise
  if (!observabilityToolType) return false

  // Maps the proper IntegrationType of the Health Check based on the selected domain
  const derivedIntegrationType =
    getIntegrationTypeFromObservabilityToolTypeAndDomain(
      observabilityToolType,
      domain
    )

  return (
    derivedIntegrationType === integrationType &&
    statusCheckName === observabilityToolName
  )
}

/*
 * In the Dynatrace integration type, we have data mismatches between the status check name
 * 'DynaTrace' (which is incorrect), and the observability tool name 'Dynatrace' (correct).
 * So we want to normalize the HC and Observability Tool names prior to comparison.
 */
function existingStatusCheckPredicateFnDynatrace(
  { integration }: ServiceStatusCheckFromStatusCheck,
  observabilityTool: ObservabilityTool
) {
  return (
    integration.type === observabilityTool.integrationType &&
    integration.name.toLowerCase() === observabilityTool.name.toLowerCase()
  )
}

function existingStatusCheckPredicateFnDefault(
  statusCheck: ServiceStatusCheckFromStatusCheck,
  observabilityTool: ObservabilityTool
) {
  return (
    statusCheck.integration.type === observabilityTool.integrationType &&
    statusCheck.integration.name === observabilityTool.name
  )
}

/*
 * Health Check URL Extraction Transformation Functions
 * -----   -----   -----
 * When extracting URLs from the available Health Checks, these transformation functions will
 * be applied to all Health Check URLs that were not filtered out by the predicate functions.
 *
 * Transform functions should include logic to check whether the transformation should be applied
 * to the reference Health Check or not.
 */

function existingStatusCheckTransformFnAws({
  integration
}: ServiceStatusCheckFromStatusCheck) {
  return encodeQueryParams(convertToAWSApiUrl(integration.url ?? ''))
}

function existingStatusCheckTransformFnDefault(
  statusCheck: ServiceStatusCheckFromStatusCheck
) {
  return statusCheck.integration.url
}

/*
 * Utility/Helper functions
 */

export function filterExistingStatusChecksByPredicate(
  statusCheck: ServiceStatusCheckFromStatusCheck,
  observabilityTool: ObservabilityTool,
  requestMethod: EndpointConfiguration['method'],
  predicateFn: PredicateFunction
) {
  const { integration } = statusCheck
  const { observabilityToolType, url } = integration

  if (!observabilityToolType || !url) return false

  return predicateFn(statusCheck, observabilityTool, requestMethod)
}

/**
 * Extracts URLs from status checks based on the observability tool and request method.
 * Optionally filters out any status checks that don't meet the supplied predicate function.
 * Transforms the Health Check's URL field as specified in the `transformUrlFn` argument.
 */
export function extractUrlsFromStatusChecks(
  statusChecks: ServiceStatusCheckFromStatusCheck[],
  observabilityTool: ObservabilityTool,
  requestMethod: EndpointConfiguration['method'],
  predicateFn: PredicateFunction,
  transformUrlFn: TransformFunction
) {
  return (
    statusChecks
      // Filter out any `statusChecks` which match the selected observability tool type, name and method
      .filter((statusCheck) =>
        filterExistingStatusChecksByPredicate(
          statusCheck,
          observabilityTool,
          requestMethod,
          predicateFn
        )
      )
      // extract all resulting statusCheck monitor URLs to a string array
      .map(transformUrlFn)
  )
}

export function getExistingMonitorUrlPredicateFn(
  observabilityToolType: ObservabilityToolType | undefined
): PredicateFunction {
  switch (observabilityToolType) {
    case ObservabilityToolType.CUSTOM:
      return existingStatusCheckPredicateFnCustom
    case ObservabilityToolType.DATADOG:
      return existingStatusCheckPredicateFnDatadog
    case ObservabilityToolType.DYNATRACE:
      return existingStatusCheckPredicateFnDynatrace
    default:
      return existingStatusCheckPredicateFnDefault
  }
}

export function getExistingMonitorUrlTransformFn(
  observabilityToolType: ObservabilityToolType | undefined
) {
  switch (observabilityToolType) {
    // AWS observability tool types require URL transformations prior to comparison.
    case ObservabilityToolType.AWS:
      return existingStatusCheckTransformFnAws
    // This will simply return the Health Check's integration URL field value
    default:
      return existingStatusCheckTransformFnDefault
  }
}

export function validateMonitorUrl(
  value: string,
  integrationType: IntegrationType,
  isPrivateNetworkIntegration: boolean
) {
  // Prometheus URLs cannot guarantee to match any specific regex pattern
  if (value.trim() && integrationType === IntegrationType.Prometheus)
    return true

  if (!isValidUrl(value)) return false

  const defaultRegexPatterns = allowedIntegrationURLs[integrationType]
  const regexPatterns = isPrivateNetworkIntegration
    ? allowedIntegrationURLsWithPNI[integrationType] ?? defaultRegexPatterns
    : defaultRegexPatterns

  return regexPatterns.some((u) => u.test(value))
}
